/**
 * A form input for entering a number with an attached keypad.
 * Handles keyboard input and state internally, firing call backs when a
 * number is entered
 */

import React from 'react';
import PropTypes from 'prop-types';
import IPropTypes from 'react-immutable-proptypes';
import { findDOMNode } from 'react-dom';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import classNames from 'classnames';

import { KeypadElement } from 'phoenix2/components/widgets/keypad';
import { ValidatedNumberEntry } from 'phoenix2/utils/validated-number-entry';
import { shouldKeyTrigger } from 'phoenix2/utils/keyboard';
import { CheckingLabel } from './checking-label.jsx';

/**
 * function to determine keypad sign properties based on the numeric question
 * sign restriction. This is called by components which use KeypadNumberInput.
 * @param sign: a restriction on the sign of a numeric answer. Can be either
 * 'positive', 'negative' or undefined.
 */
const parseSignRestricted = sign => {
    const allowNegative = sign !== 'positive' && sign !== 'negative';
    const enforceNegative = sign === 'negative';
    return { allowNegative, enforceNegative };
};

class KeypadNumberInput extends React.Component {
    static displayName = 'KeypadNumberInput';

    static propTypes = {
        minCharacters: PropTypes.number.isRequired,
        maxCharacters: PropTypes.number.isRequired,
        allowNegative: PropTypes.bool.isRequired,
        allowDecimal: PropTypes.bool.isRequired,
        allowEnter: PropTypes.bool.isRequired,
        onChange: PropTypes.func.isRequired,
        onSubmit: PropTypes.func,
        className: PropTypes.string,
        enableAnimation: PropTypes.bool.isRequired,
        gridKeypadMinHeight: PropTypes.number,
        hideDigits: PropTypes.bool.isRequired,
        value: PropTypes.string.isRequired,

        // Whether to only allow "-", "-0", or numbers less than 0
        enforceNegative: PropTypes.bool,

        // Whether to hide the keypad and only show the number field
        // (defaults to false)
        hideKeypad: PropTypes.bool.isRequired,

        // Whether to disable update of the input e.g. when the answer is
        // already submitted the user should not be able to make further changes
        isDisabled: PropTypes.bool,

        // Whether to layout the keypad as a "grid" or a "line" - if not set,
        // will change depending on screen size
        keypadLayout: PropTypes.string,

        // Whether to show a blinking cursor in the field. Note: this cannot
        // be used with hideDigits
        showCursor: PropTypes.bool,

        // Inline styles to put on the keypad element:
        keypadStyle: PropTypes.object,

        // Inline styles for the number input:
        style: PropTypes.object,

        // Callback for if the element is keyboard focussed and submitted. If
        // supplied, the field is given a tabindex
        onKeyboardSubmit: PropTypes.func,

        // Checking mode information to display. Overrides the value shown
        // if set:
        checkingModeMetadata: IPropTypes.map,
    };

    static defaultProps = {
        minCharacters: 1,
        maxCharacters: 12,
        allowDecimal: true,
        allowNegative: true,
        enforceNegative: false,
        allowEnter: false,
        enableAnimation: true,
        hideDigits: false,
        value: '',
        hideKeypad: false,
        showCursor: false,
    };

    constructor(props) {
        super(props);

        const {
            minCharacters,
            maxCharacters,
            allowNegative,
            enforceNegative,
            allowDecimal,
            value,
        } = this.props;

        // Utility class for creating a number entry
        this._entry = new ValidatedNumberEntry({
            minCharacters,
            maxCharacters,
            allowNegative,
            allowDecimal,
            enforceNegative,
            entry: value,
        });

        this.state = {
            numberString: this._entry.getEntry(),
            valid: this._entry.isValid(),
        };
    }

    render() {
        const {
            allowDecimal,
            allowNegative,
            allowEnter,
            className,
            enableAnimation,
            gridKeypadMinHeight,
            hideDigits,
            hideKeypad,
            keypadLayout,
            showCursor,
            keypadStyle,
            style,
            onKeyboardSubmit,
            checkingModeMetadata,
        } = this.props;

        const { numberString } = this.state;

        const contents = [];

        const tabIndex = onKeyboardSubmit ? '0' : null;

        if (checkingModeMetadata) {
            const checkingLabel = checkingModeMetadata ? (
                <CheckingLabel key="checking-label" checkingModeMetadata={checkingModeMetadata} />
            ) : null;

            contents.push(checkingLabel);
        } else {
            let inputField = null;

            if (showCursor && !hideDigits) {
                // If showing a cursor (can't be used with hideDigits), make the
                // input a div which we put a cursor in after the input value:
                inputField = (
                    <div
                        key="input-field"
                        className="number-input"
                        ref={el => (this._input = el)}
                        onFocus={this.onFocusInput.bind(this)}
                    >
                        {numberString}
                        <span className="number-input-cursor">|</span>
                    </div>
                );
            } else {
                inputField = (
                    <input
                        key="input-field"
                        className="number-input"
                        tabIndex="-1"
                        value={numberString}
                        type={hideDigits ? 'password' : 'text'}
                        maxLength={50}
                        size="15"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        ref={el => (this._input = el)}
                        onFocus={this.onFocusInput.bind(this)}
                        readOnly={true}
                    />
                );
            }

            contents.push(inputField);

            if (!hideKeypad) {
                contents.push(
                    <KeypadElement
                        key="keypad"
                        ref={el => (this._keypad = el)}
                        onButtonPressed={this.onButtonPressed.bind(this)}
                        handleKeyboard={true}
                        allowDecimalPoint={allowDecimal}
                        allowMinusSign={allowNegative}
                        allowEnter={allowEnter}
                        enableDecimalPoint={this._entry.getEntry().indexOf('.') === -1}
                        enableMinusSign={true}
                        enableBackspace={this._entry.isBackspaceAllowed()}
                        enableEnter={this._entry.isValid()}
                        enableAnimation={enableAnimation}
                        gridKeypadMinHeight={gridKeypadMinHeight}
                        layout={keypadLayout}
                        style={keypadStyle}
                    />,
                );
            }
        }

        let classnames = classNames('keypad-number-input', {
            'checking-mode': checkingModeMetadata,
        });

        if (className) {
            classnames = classnames + ' ' + className + '';
        }

        return (
            <div
                className={classnames}
                ref={el => (this._keypadNumberInput = el)}
                style={style}
                tabIndex={tabIndex}
                onKeyDown={evt =>
                    shouldKeyTrigger(evt) && onKeyboardSubmit && onKeyboardSubmit(evt)
                }
                onMouseDown={evt => {
                    evt.preventDefault();
                }}
            >
                {contents}
            </div>
        );
    }

    onFocusInput() {
        this._input.blur();

        if (this._keypad) {
            // Scroll the keypad into view
            scrollIntoViewIfNeeded(findDOMNode(this._keypad), false, {
                duration: 500,
                easing: 'easeInOut',
            });
        }
    }

    /**
     * Responds to a button press or keyboard press from the keypad. Returns
     * true if this changed the number input entry (the keypad has a
     * different animation depending on whether the button press was
     * successful or not)
     * @param evt
     * @param button the button that was pressed
     * @returns {boolean}
     */
    onButtonPressed(evt, button) {
        // If the keypad is disbabled do not do anything
        if (this.props.isDisabled) {
            return;
        }
        // If the button being pressed is Enter, and the entry is valid,
        // fire the onSubmit callback:
        if (button === 'Enter') {
            if (this.props.onSubmit && this._entry.isValid()) {
                this.props.onSubmit(this._entry.getEntry());
                return true;
            }
            return false;
        }

        // Prevent some browsers (e.g. Firefox) navigating back in indyclient
        if (button === 'Backspace') {
            evt.preventDefault();
        }

        // Apply any other character to the entry:
        const changed = this._entry.applyKeypress(button);

        // If the button press changed the entry, fire the callback, and
        // change the value shown in the input
        if (changed) {
            const numberString = this._entry.getEntry();
            const valid = this._entry.isValid();
            this.props.onChange(numberString, this._entry.isValid());
            this.setState({ numberString, valid });
            return true;
        }
        return false;
    }

    domNode() {
        return this._keypadNumberInput;
    }

    focus() {
        this._keypadNumberInput.focus();
    }
}

export { KeypadNumberInput, parseSignRestricted };
